import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import DataTable, { type ExpanderComponentProps, type TableColumn, type TableProps } from "react-data-table-component";
import { Modal } from "../../components/admin/Modal";
import { PageIntro } from "../../components/admin/PageIntro";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import adminStyles from "../../components/admin/Admin.module.css";
import panel from "../../components/admin/AdminPanel.module.css";
import { RelativeTimestamp } from "../../components/RelativeTimestamp";
import { TablePager } from "../../components/shared/TablePager";
import { Tooltip } from "../../components/shared/Tooltip";
import { useColumnFilters, type ColumnFilterSpec } from "../../components/shared/columnFilters";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { OverrideDialog, type OverrideDialogMode } from "../../components/bedrockPrices/OverrideDialog";
import { formatRate } from "../../components/bedrockPrices/rateInput";
import styles from "../../components/bedrockPrices/BedrockPrices.module.css";
import {
  createBedrockPriceOverride,
  deleteBedrockPriceOverride,
  fetchBedrockPrices,
  updateBedrockPriceOverride,
  type BedrockAwsRate,
  type BedrockPriceCatalogue,
  type BedrockPriceOverride,
  type BedrockPriceResult,
  type BedrockPriceRow,
  type SaveBedrockPriceOverride,
} from "../../api/bedrockPrices";

const SettingsWritePermission = "settings.write";
const PageSize = 25;
const PageSizes = [25, 50, 100];

type State = { kind: "loading" } | { kind: "loaded"; catalogue: BedrockPriceCatalogue } | { kind: "error"; message: string };
type Dialog = null | { kind: "form"; mode: OverrideDialogMode } | { kind: "delete"; override: BedrockPriceOverride };

/** A price row with the key the table tells it apart by: model, route, Region and source together. */
type PriceRow = BedrockPriceRow & { key: string };

/** A model nothing prices, as its table shows it: from Bedrock's model list, or only from the invocations when that list is unavailable. */
interface UnpricedRow {
  modelId: string;
  displayName: string;
  invocationCount: number;
  lastSeenAt: string | null;
}

/** The text of a call that did not succeed, for a failed load. */
function failureText(result: Exclude<BedrockPriceResult<unknown>, { kind: "ok" }>, t: TFunction): string {
  switch (result.kind) {
    case "denied":
      return t("admin.denied.description", { permissions: SettingsWritePermission });
    case "invalid":
    case "conflict":
    case "error":
      return result.message;
    default:
      return t("bedrockPrices.loadFailed");
  }
}

/** The AWS rate in one line: input and output, the two rates every price has. */
function awsRateLine(rate: BedrockAwsRate, locale: string, t: TFunction): string {
  return t("bedrockPrices.awsRate.line", { input: formatRate(rate.inputPerMillion, locale), output: formatRate(rate.outputPerMillion, locale) });
}

/** The AWS rate in full, for the tooltip, the badge's name and the expanded row: the route and Region, then every rate AWS publishes. */
function awsRateDetail(rate: BedrockAwsRate, locale: string, t: TFunction): string {
  const optional = (value: number | null): string => (value === null ? t("bedrockPrices.none") : formatRate(value, locale));
  return t("bedrockPrices.awsRate.detail", {
    route: rate.route,
    region: rate.region,
    input: formatRate(rate.inputPerMillion, locale),
    output: formatRate(rate.outputPerMillion, locale),
    cacheRead: optional(rate.cacheReadPerMillion),
    cacheWrite5m: optional(rate.cacheWrite5mPerMillion),
    cacheWrite1h: optional(rate.cacheWrite1hPerMillion),
  });
}

/** The source of a price row in words: CUR or AWS. */
function sourceText(row: BedrockPriceRow, t: TFunction): string {
  return row.source.toLowerCase() === "cur" ? t("bedrockPrices.source.cur") : t("bedrockPrices.source.aws");
}

/** The state of an override in words. */
function stateText(entry: BedrockPriceOverride, t: TFunction): string {
  return entry.state === "InUse" ? t("bedrockPrices.state.inUse") : t("bedrockPrices.state.noEffect");
}

/**
 * An override's details beneath its row: the cache rates, the source note and, where AWS prices the model, the AWS price in full.
 * Rendered by the table's expander, so it takes the locale and translator from the hooks rather than from the row.
 */
function OverrideDetails({ data }: ExpanderComponentProps<BedrockPriceOverride>): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const optional = (value: number | null): string => (value === null ? t("bedrockPrices.none") : formatRate(value, i18n.language));
  return (
    <dl className={adminStyles.syncRunDetails}>
      <div>
        <dt>{t("bedrockPrices.columns.cacheRead")}</dt>
        <dd className={styles.number}>{optional(data.cacheReadPerMillion)}</dd>
      </div>
      <div>
        <dt>{t("bedrockPrices.columns.cacheWrite")}</dt>
        <dd className={styles.number}>{optional(data.cacheWritePerMillion)}</dd>
      </div>
      <div>
        <dt>{t("bedrockPrices.columns.source")}</dt>
        <dd>{data.source ?? t("bedrockPrices.noSource")}</dd>
      </div>
      <div>
        <dt>{t("bedrockPrices.columns.awsRate")}</dt>
        <dd>{data.awsRate === null ? t("bedrockPrices.awsRate.none") : awsRateDetail(data.awsRate, i18n.language, t)}</dd>
      </div>
    </dl>
  );
}

/**
 * The "Bedrock prices" tab of Providers (AIA-109 pricing page): the prices the real-time spend estimate uses, and the operator overrides for
 * a model AWS publishes no price for. A summary panel leads with the Price List refresh and the counts, then three tables in the portal's
 * shared table control: the overrides (added, edited and deleted here, their details in an expanded row), the AWS and CUR prices, and the
 * models nothing prices. Each table sorts, filters by column and pages as the other admin tables do. AWS and CUR rates are read-only. The
 * page rereads the catalogue after every change rather than patching what it holds, a stale save (409) is told by toast and also rereads,
 * and every outcome is a toast. The Providers screen shows the tab only to a person holding `settings.write`, the one permission that reads
 * and writes prices.
 */
export function BedrockPricesContent(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const locale = i18n.language;
  const [state, setState] = useState<State>({ kind: "loading" });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchBedrockPrices();
    setState(result.kind === "ok" ? { kind: "loaded", catalogue: result.response } : { kind: "error", message: failureText(result, t) });
  }, [t]);

  /** Reads the catalogue again behind what is on screen, so a dialog or table is not torn down; a failure keeps the old data and says so. */
  const refresh = useCallback(async () => {
    const result = await fetchBedrockPrices();
    if (result.kind === "ok") {
      setState({ kind: "loaded", catalogue: result.response });
    } else {
      toast.show(t("bedrockPrices.reloadFailed"), "error");
    }
  }, [t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const catalogue = state.kind === "loaded" ? state.catalogue : null;

  const priceRows = useMemo<PriceRow[]>(
    () => (catalogue === null ? [] : catalogue.prices.map((row) => ({ ...row, key: `${row.modelKey}|${row.route}|${row.region}|${row.source}` }))),
    [catalogue],
  );
  const overrideRows = useMemo<BedrockPriceOverride[]>(() => catalogue?.overrides ?? [], [catalogue]);
  const unpricedRows = useMemo<UnpricedRow[]>(() => {
    if (catalogue === null) {
      return [];
    }
    if (!catalogue.modelsAvailable) {
      return catalogue.unpriced.map((model) => ({ modelId: model.modelId, displayName: "", invocationCount: model.invocationCount, lastSeenAt: model.lastSeenAt }));
    }
    const invoked = new Map(catalogue.unpriced.map((model) => [model.modelId, model]));
    return catalogue.models
      .filter((model) => model.priceSource === "None")
      .map((model) => {
        const seen = invoked.get(model.modelId);
        return { modelId: model.modelId, displayName: model.displayName, invocationCount: seen?.invocationCount ?? 0, lastSeenAt: seen?.lastSeenAt ?? null };
      });
  }, [catalogue]);

  const priceFilters = useMemo<ColumnFilterSpec<PriceRow>[]>(
    () => [
      { id: "model", kind: "text", value: (row) => row.modelKey },
      { id: "route", kind: "set", value: (row) => row.route },
      { id: "region", kind: "set", value: (row) => row.region },
      { id: "source", kind: "set", value: (row) => sourceText(row, t) },
    ],
    [t],
  );
  const overrideFilters = useMemo<ColumnFilterSpec<BedrockPriceOverride>[]>(
    () => [
      { id: "model", kind: "text", value: (row) => row.modelId },
      { id: "state", kind: "set", value: (row) => stateText(row, t) },
    ],
    [t],
  );
  const unpricedFilters = useMemo<ColumnFilterSpec<UnpricedRow>[]>(
    () => [
      { id: "model", kind: "text", value: (row) => row.modelId },
      { id: "name", kind: "text", value: (row) => row.displayName },
    ],
    [],
  );

  const { filtered: shownPrices, heading: priceHeading } = useColumnFilters(priceRows, priceFilters);
  const { filtered: shownOverrides, heading: overrideHeading } = useColumnFilters(overrideRows, overrideFilters);
  const { filtered: shownUnpriced, heading: unpricedHeading } = useColumnFilters(unpricedRows, unpricedFilters);

  const openCreate = useCallback((modelId: string) => setDialog({ kind: "form", mode: { kind: "create", modelId } }), []);

  const overrideColumns = useMemo<TableColumn<BedrockPriceOverride>[]>(
    () => [
      { id: "model", name: overrideHeading("model", t("bedrockPrices.columns.model")), selector: (row) => row.modelId, sortable: true, wrap: true, grow: 2, cell: (row) => <span className={styles.modelId}>{row.modelId}</span> },
      { id: "input", name: t("bedrockPrices.columns.inputShort"), selector: (row) => row.inputPerMillion, sortable: true, right: true, cell: (row) => <span className={styles.number}>{formatRate(row.inputPerMillion, locale)}</span> },
      { id: "output", name: t("bedrockPrices.columns.outputShort"), selector: (row) => row.outputPerMillion, sortable: true, right: true, cell: (row) => <span className={styles.number}>{formatRate(row.outputPerMillion, locale)}</span> },
      {
        id: "state",
        name: overrideHeading("state", t("bedrockPrices.columns.state")),
        selector: (row) => row.state,
        sortable: true,
        allowOverflow: true,
        cell: (row) => {
          const text = stateText(row, t);
          if (row.awsRate === null) {
            return (
              <span className={styles.badge} data-state={row.state}>
                <span className={styles.badgeDot} aria-hidden="true" />
                {text}
              </span>
            );
          }
          const detail = awsRateDetail(row.awsRate, locale, t);
          return (
            <Tooltip label={detail} align="start">
              <button type="button" className={styles.badge} data-state={row.state} aria-label={`${text}. ${detail}`}>
                <span className={styles.badgeDot} aria-hidden="true" />
                {text}
              </button>
            </Tooltip>
          );
        },
      },
      {
        id: "awsRate",
        name: t("bedrockPrices.columns.awsRate"),
        selector: (row) => row.awsRate?.inputPerMillion ?? -1,
        sortable: true,
        cell: (row) => (row.awsRate === null ? <span className={styles.muted}>{t("bedrockPrices.awsRate.none")}</span> : <span className={styles.number}>{awsRateLine(row.awsRate, locale, t)}</span>),
      },
      {
        id: "reviewBy",
        name: t("bedrockPrices.columns.reviewBy"),
        selector: (row) => row.reviewBy ?? "",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <span>
            {row.reviewBy ?? <span className={styles.muted}>{t("bedrockPrices.noReviewDate")}</span>}
            {row.reviewOverdue && <span className={styles.overdue}>{t("bedrockPrices.reviewOverdue")}</span>}
          </span>
        ),
      },
      {
        id: "actions",
        name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
        button: true,
        ignoreRowClick: true,
        minWidth: "11rem",
        cell: (row) => (
          <div className={styles.actions}>
            <button type="button" className="button button-secondary" onClick={() => setDialog({ kind: "form", mode: { kind: "edit", override: row } })} aria-label={t("bedrockPrices.overrides.editFor", { model: row.modelId })}>
              {t("bedrockPrices.overrides.edit")}
            </button>
            <button type="button" className="button button-secondary" onClick={() => setDialog({ kind: "delete", override: row })} aria-label={t("bedrockPrices.overrides.deleteFor", { model: row.modelId })}>
              {t("bedrockPrices.overrides.delete")}
            </button>
          </div>
        ),
      },
    ],
    [t, locale, overrideHeading],
  );

  const priceColumns = useMemo<TableColumn<PriceRow>[]>(() => {
    const optional = (value: number | null): string => (value === null ? t("bedrockPrices.none") : formatRate(value, locale));
    const rate = (id: string, label: string, value: (row: PriceRow) => number | null): TableColumn<PriceRow> => ({
      id,
      name: label,
      selector: (row) => value(row) ?? -1,
      sortable: true,
      right: true,
      cell: (row) => <span className={styles.number}>{optional(value(row))}</span>,
    });
    return [
      { id: "model", name: priceHeading("model", t("bedrockPrices.columns.model")), selector: (row) => row.modelKey, sortable: true, wrap: true, grow: 2 },
      { id: "route", name: priceHeading("route", t("bedrockPrices.columns.route")), selector: (row) => row.route, sortable: true },
      { id: "region", name: priceHeading("region", t("bedrockPrices.columns.region")), selector: (row) => row.region, sortable: true },
      rate("input", t("bedrockPrices.columns.inputShort"), (row) => row.inputPerMillion),
      rate("output", t("bedrockPrices.columns.outputShort"), (row) => row.outputPerMillion),
      rate("cacheRead", t("bedrockPrices.columns.cacheReadShort"), (row) => row.cacheReadPerMillion),
      rate("cacheWrite5m", t("bedrockPrices.columns.cacheWrite5mShort"), (row) => row.cacheWrite5mPerMillion),
      rate("cacheWrite1h", t("bedrockPrices.columns.cacheWrite1hShort"), (row) => row.cacheWrite1hPerMillion),
      { id: "source", name: priceHeading("source", t("bedrockPrices.columns.source")), selector: (row) => row.source, sortable: true, cell: (row) => sourceText(row, t) },
      // The API sends an ISO timestamp in UTC, so its first ten characters are the UTC date.
      { id: "derivedOn", name: t("bedrockPrices.columns.derivedOn"), selector: (row) => row.derivedAt, sortable: true, cell: (row) => row.derivedAt.slice(0, 10) },
    ];
  }, [t, locale, priceHeading]);

  const unpricedColumns = useMemo<TableColumn<UnpricedRow>[]>(() => {
    const withNames = catalogue?.modelsAvailable ?? true;
    return [
      { id: "model", name: unpricedHeading("model", t("bedrockPrices.columns.model")), selector: (row) => row.modelId, sortable: true, wrap: true, grow: 2, cell: (row) => <span className={styles.modelId}>{row.modelId}</span> },
      ...(withNames ? [{ id: "name", name: unpricedHeading("name", t("bedrockPrices.columns.name")), selector: (row: UnpricedRow) => row.displayName, sortable: true, wrap: true, grow: 2 }] : []),
      { id: "invocations", name: t("bedrockPrices.columns.invocations"), selector: (row) => row.invocationCount, sortable: true, right: true, cell: (row) => <span className={styles.number}>{row.invocationCount.toLocaleString(locale)}</span> },
      {
        id: "lastSeen",
        name: t("bedrockPrices.columns.lastSeen"),
        selector: (row) => row.lastSeenAt ?? "",
        sortable: true,
        cell: (row) => (row.lastSeenAt === null ? <span className={styles.muted}>{t("bedrockPrices.neverInvoked")}</span> : <RelativeTimestamp isoTimestamp={row.lastSeenAt} />),
      },
      {
        id: "actions",
        name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
        button: true,
        ignoreRowClick: true,
        minWidth: "9rem",
        cell: (row) => (
          <button type="button" className="button button-secondary" onClick={() => openCreate(row.modelId)} aria-label={t("bedrockPrices.unpriced.addFor", { model: row.modelId })}>
            {t("bedrockPrices.overrides.add")}
          </button>
        ),
      },
    ];
  }, [t, locale, unpricedHeading, catalogue, openCreate]);

  /** Sends a create or an update, and tells each outcome by toast. Answers null to close the dialog, else the field the server refused. */
  const save = async (body: SaveBedrockPriceOverride): Promise<{ field: string | null } | null> => {
    if (dialog?.kind !== "form") {
      return null;
    }

    setBusy(true);
    const mode = dialog.mode;
    const result = mode.kind === "edit" ? await updateBedrockPriceOverride(mode.override.id, body) : await createBedrockPriceOverride(body);
    setBusy(false);

    switch (result.kind) {
      case "ok":
        toast.show(t(mode.kind === "edit" ? "bedrockPrices.updated" : "bedrockPrices.created", { model: result.response.override.modelId, note: result.response.note }), "success");
        setDialog(null);
        await refresh();
        return null;
      case "stale":
        toast.show(t("bedrockPrices.stale", { model: mode.kind === "edit" ? mode.override.modelId : body.modelId }), "error");
        setDialog(null);
        await refresh();
        return null;
      case "notFound":
        toast.show(t("bedrockPrices.gone"), "error");
        setDialog(null);
        await refresh();
        return null;
      case "invalid":
        toast.show(result.message, "error");
        return { field: result.field };
      case "conflict":
      case "error":
        toast.show(result.message, "error");
        return { field: null };
      default:
        toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
        return { field: null };
    }
  };

  const confirmDelete = async (target: BedrockPriceOverride): Promise<void> => {
    setBusy(true);
    const result = await deleteBedrockPriceOverride(target.id, target.version);
    setBusy(false);
    setDialog(null);

    switch (result.kind) {
      case "ok":
        toast.show(t("bedrockPrices.deleted", { model: target.modelId }), "success");
        break;
      case "stale":
        toast.show(t("bedrockPrices.stale", { model: target.modelId }), "error");
        break;
      case "notFound":
        toast.show(t("bedrockPrices.gone"), "error");
        break;
      case "denied":
        toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
        break;
      default:
        toast.show(result.kind === "error" || result.kind === "conflict" || result.kind === "invalid" ? result.message : t("bedrockPrices.loadFailed"), "error");
    }

    await refresh();
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("bedrockPrices.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("bedrockPrices.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  const data = state.catalogue;
  const refreshRun = data.refresh;
  const neverRefreshed = refreshRun.lastRunStartedAt === null;
  const refreshAttention = neverRefreshed || refreshRun.hadProblems || data.prices.length === 0;
  const curCount = data.prices.filter((row) => row.source.toLowerCase() === "cur").length;
  const noEffectCount = data.overrides.filter((entry) => entry.state === "NoEffect").length;
  const overdueCount = data.overrides.filter((entry) => entry.reviewOverdue).length;

  const table = <T,>(rows: T[], columns: TableColumn<T>[], keyField: keyof T & string, label: string, extra: Partial<TableProps<T>> = {}): React.JSX.Element => (
    <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
      <DataTable
        persistTableHead
        columns={columns}
        data={rows}
        className={adminStyles.peopleDataTable}
        keyField={keyField}
        ariaLabel={label}
        theme={adminTableTheme}
        striped
        highlightOnHover
        columnSeparator="subtle"
        noDataComponent={<p className={adminStyles.note}>{t("bedrockPrices.noMatches")}</p>}
        pagination={rows.length > PageSize}
        paginationComponent={TablePager}
        paginationPerPage={PageSize}
        paginationRowsPerPageOptions={PageSizes}
        {...extra}
      />
    </div>
  );

  return (
    <div className={styles.page}>
      <PageIntro>{t("bedrockPrices.intro")}</PageIntro>

      <section className={`${panel.panel} ${styles.summary}`} aria-labelledby="bedrock-prices-summary-heading">
        <div className={panel.header}>
          <h2 id="bedrock-prices-summary-heading" className={`panel-title ${panel.title}`}>
            {t("bedrockPrices.summary.title")}
          </h2>
        </div>
        <dl className={panel.tiles}>
          <div className={panel.tile} data-attention={refreshAttention || undefined}>
            <dt>{t("bedrockPrices.summary.refresh")}</dt>
            <dd>
              {neverRefreshed ? t("bedrockPrices.refresh.neverShort") : (refreshRun.outcome ?? t("bedrockPrices.refresh.running"))}
              <span className={panel.tileNote}>
                {neverRefreshed ? (
                  t("bedrockPrices.refresh.never")
                ) : (
                  <>
                    <RelativeTimestamp isoTimestamp={refreshRun.lastRunFinishedAt ?? refreshRun.lastRunStartedAt ?? ""} />
                    {refreshRun.hadProblems && `, ${t("bedrockPrices.refresh.problems")}`}
                  </>
                )}
              </span>
            </dd>
          </div>
          <div className={panel.tile} data-attention={data.prices.length === 0 || undefined}>
            <dt>{t("bedrockPrices.summary.prices")}</dt>
            <dd>
              {data.prices.length.toLocaleString(locale)}
              <span className={panel.tileNote}>
                {data.prices.length === 0 ? t("bedrockPrices.refresh.noPrices") : t("bedrockPrices.summary.pricesNote", { aws: data.prices.length - curCount, cur: curCount })}
              </span>
            </dd>
          </div>
          <div className={panel.tile} data-attention={overdueCount > 0 || undefined}>
            <dt>{t("bedrockPrices.summary.overrides")}</dt>
            <dd>
              {data.overrides.length.toLocaleString(locale)}
              <span className={panel.tileNote}>
                {overdueCount > 0 ? t("bedrockPrices.summary.overdueNote", { count: overdueCount }) : t("bedrockPrices.summary.overridesNote", { count: noEffectCount })}
              </span>
            </dd>
          </div>
          <div className={panel.tile} data-attention={!data.modelsAvailable || unpricedRows.length > 0 || undefined}>
            <dt>{t("bedrockPrices.summary.unpriced")}</dt>
            <dd>
              {unpricedRows.length.toLocaleString(locale)}
              <span className={panel.tileNote}>{data.modelsAvailable ? t("bedrockPrices.summary.unpricedNote") : t("bedrockPrices.modelsUnavailable")}</span>
            </dd>
          </div>
        </dl>
      </section>

      <section className={styles.section} aria-labelledby="bedrock-overrides-heading">
        <div className={styles.sectionHead}>
          <h2 id="bedrock-overrides-heading" className={`panel-title ${styles.sectionTitle}`}>
            {t("bedrockPrices.overrides.title")}
          </h2>
          <button type="button" className="button button-primary" onClick={() => openCreate("")}>
            {t("bedrockPrices.overrides.add")}
          </button>
        </div>
        <p className={styles.sectionNote}>{t("bedrockPrices.overrides.note")}</p>
        {data.overrides.length === 0 ? (
          <EmptyState title={t("bedrockPrices.overrides.emptyTitle")} description={t("bedrockPrices.overrides.emptyDescription")} />
        ) : (
          table(shownOverrides, overrideColumns, "id", t("bedrockPrices.overrides.title"), {
            expandableRows: true,
            expandableRowsComponent: OverrideDetails,
            defaultSortFieldId: "model",
          })
        )}
      </section>

      <section className={styles.section} aria-labelledby="bedrock-prices-heading">
        <div className={styles.sectionHead}>
          <h2 id="bedrock-prices-heading" className={`panel-title ${styles.sectionTitle}`}>
            {t("bedrockPrices.prices.title")}
          </h2>
        </div>
        <p className={styles.sectionNote}>{t("bedrockPrices.prices.note")}</p>
        {data.prices.length === 0 ? (
          <EmptyState title={t("bedrockPrices.prices.emptyTitle")} description={t("bedrockPrices.prices.emptyDescription")} />
        ) : (
          table(shownPrices, priceColumns, "key", t("bedrockPrices.prices.title"), { defaultSortFieldId: "model" })
        )}
      </section>

      <section className={styles.section} aria-labelledby="bedrock-unpriced-heading">
        <div className={styles.sectionHead}>
          <h2 id="bedrock-unpriced-heading" className={`panel-title ${styles.sectionTitle}`}>
            {t("bedrockPrices.unpriced.title")}
          </h2>
        </div>
        <p className={styles.sectionNote}>{t(data.modelsAvailable ? "bedrockPrices.unpriced.note" : "bedrockPrices.unpriced.noteInvoked")}</p>
        {unpricedRows.length === 0 ? (
          <EmptyState title={t("bedrockPrices.unpriced.emptyTitle")} description={t("bedrockPrices.unpriced.emptyDescription")} />
        ) : (
          table(shownUnpriced, unpricedColumns, "modelId", t("bedrockPrices.unpriced.title"), { defaultSortFieldId: "invocations", defaultSortAsc: false })
        )}
      </section>

      {dialog?.kind === "form" && (
        <OverrideDialog
          key={dialog.mode.kind === "edit" ? `${dialog.mode.override.id}:${String(dialog.mode.override.version)}` : `new:${dialog.mode.modelId}`}
          mode={dialog.mode}
          models={data.modelsAvailable ? data.models : null}
          busy={busy}
          onSubmit={save}
          onCancel={() => setDialog(null)}
          onInvalid={() => toast.show(t("bedrockPrices.dialog.invalid"), "error")}
        />
      )}

      {dialog?.kind === "delete" && (
        <Modal title={t("bedrockPrices.delete.title")} onClose={() => setDialog(null)} closeOnBackdropClick={false}>
          <p>
            {t("bedrockPrices.delete.question")} <span className={styles.modelId}>{dialog.override.modelId}</span>
          </p>
          <p>{t("bedrockPrices.delete.effect")}</p>
          <div className={styles.dialogActions}>
            <button type="button" className="button button-secondary" onClick={() => setDialog(null)} disabled={busy} data-autofocus>
              {t("bedrockPrices.dialog.cancel")}
            </button>
            <button type="button" className="button button-primary" onClick={() => void confirmDelete(dialog.override)} disabled={busy}>
              {t("bedrockPrices.delete.confirm")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
